import Vue from 'vue';
import VueRouter from 'vue-router';
import { APP_ROLES, PAGE_NAMES } from '@/constants';
import { RouteType } from '@/models';
import { canVisitAdminPage, canVisitProjectAdminPage, hasToken } from '@/services';
import store from '@/store';

Vue.use(VueRouter);

const routes: Array<RouteType> = [
  {
    path: '/',
    name: PAGE_NAMES.HOME,
    icon: 'icons/home.svg',
    component: () => import(/* webpackChunkName: "home" */ '../components/pages/home/Home.vue'),
    showItemInSideBar: true
  },
  {
    path: '/login',
    name: PAGE_NAMES.LOGIN,
    component: () => import(/* webpackChunkName: "login" */ '../components/pages/login/Login.vue'),
    showItemInSideBar: false
  },
  {
    path: '/projects',
    icon: 'icons/flower.svg',
    name: PAGE_NAMES.PROJECT_LIST,
    component: () => import(/* webpackChunkName: "project-list" */ '../components/pages/project-list/ProjectList.vue'),
    showItemInSideBar: true
  },

  {
    path: '/rewards',
    name: PAGE_NAMES.REWARDS,
    icon: 'icons/honey.svg',
    component: () => import(/* webpackChunkName: "home" */ '../components/pages/rewards/Rewards.vue'),
    showItemInSideBar: true
  },
  {
    path: '/profile',
    name: PAGE_NAMES.PROFILE,
    component: () => import(/* webpackChunkName: "profile" */ '../components/pages/profile/Profile.vue'),
    icon: 'mdi-poll',
    showItemInSideBar: false
  },
  {
    path: '/initiate-evaluation',
    name: PAGE_NAMES.INITIATE_EVALUATION,
    component: () =>
      import(
        /* webpackChunkName: "initiate-evaluation" */ '../components/pages/initiate-evaluation/InitiateEvaluation.vue'
      ),
    showItemInSideBar: false,
    showInSideBarAsPage: PAGE_NAMES.ASSESSMENT_PERIODS,
    meta: {
      requiredAuthorization: true,
      roles: [APP_ROLES.ADMIN]
    }
  },
  {
    path: '/project/:id',
    name: PAGE_NAMES.PROJECT_EVALUATION,
    component: () =>
      import(
        /* webpackChunkName: "project-evaluation" */ '../components/pages/project-evaluation/ProjectEvaluation.vue'
      ),
    showItemInSideBar: false,
    showInSideBarAsPage: PAGE_NAMES.PROJECT_LIST
  },
  {
    path: '/project/:projectId/assessment/:assessmentId/adjust-individual-drops',
    name: PAGE_NAMES.ADJUST_INDIVIDUAL_DROPS,
    component: () =>
      import(
        /* webpackChunkName: "adjust-individual-drops" */ '../components/pages/adjust-individual-drops/AdjustIndividualDrops.vue'
      ),
    showItemInSideBar: false,
    showInSideBarAsPage: PAGE_NAMES.PROJECT_LIST,
    meta: {
      requiredAuthorization: true,
      roles: [APP_ROLES.ADMIN, APP_ROLES.PROJECT_ADMIN]
    }
  },
  {
    path: '/project/:projectId/assessment/:projectAssessmentId/user-evaluation-details/:userId',
    name: PAGE_NAMES.USER_EVALUATION_DETAILS,
    component: () =>
      import(
        /* webpackChunkName: "user-evaluation-details" */ '../components/pages/view-details-peer-rating/ViewDetailsPeerRating.vue'
      ),
    showItemInSideBar: false,
    showInSideBarAsPage: PAGE_NAMES.PROJECT_LIST,
    meta: {
      requiredAuthorization: true,
      roles: [APP_ROLES.ADMIN, APP_ROLES.PROJECT_ADMIN]
    }
  },
  {
    path: '/project-creation',
    name: PAGE_NAMES.PROJECT_CREATION,
    component: () =>
      import(/* webpackChunkName: "project-creation" */ '../components/pages/project-creation/ProjectCreation.vue'),
    showItemInSideBar: false,
    showInSideBarAsPage: PAGE_NAMES.PROJECT_LIST,
    meta: {
      requiredAuthorization: true,
      roles: [APP_ROLES.ADMIN]
    }
  },
  {
    path: '/project-export',
    name: PAGE_NAMES.PROJECT_EXPORT,
    component: () =>
      import(/* webpackChunkName: "project-export" */ '../components/pages/project-export/ProjectExport.vue'),
    showItemInSideBar: false,
    showInSideBarAsPage: PAGE_NAMES.PROJECT_LIST
  },
  {
    path: '/assessment-periods',
    name: PAGE_NAMES.ASSESSMENT_PERIODS,
    icon: 'icons/assessment.svg',
    component: () =>
      import(
        /* webpackChunkName: "assessment-periods" */ '../components/pages/assessment-periods/assessment-periods-list/AssessmentPeriodsList.vue'
      ),
    showItemInSideBar: true,
    showInSideBarAsPage: PAGE_NAMES.ASSESSMENT_PERIODS,
    meta: {
      requiredAuthorization: true,
      roles: [APP_ROLES.ADMIN]
    }
  },
  {
    path: '/assessment-period-detail/:id',
    name: PAGE_NAMES.ASSESSMENT_PERIOD_DETAIL,
    component: () =>
      import(
        /* webpackChunkName: "assessment-period-detail" */ '@/components/pages/assessment-periods/assessment-period-detail/AssessmentPeriodDetail.vue'
      ),
    showItemInSideBar: false,
    showInSideBarAsPage: PAGE_NAMES.ASSESSMENT_PERIODS,
    meta: {
      requiredAuthorization: true,
      roles: [APP_ROLES.ADMIN]
    }
  },
  {
    path: '/contribution-levels',
    name: PAGE_NAMES.CONTRIBUTION_LEVELS,
    icon: 'icons/level.svg',
    component: () =>
      import(
        /* webpackChunkName: "contribution-levels" */ '../components/pages/contribution-levels/ContributionLevels.vue'
      ),
    showItemInSideBar: true,
    showInSideBarAsPage: PAGE_NAMES.CONTRIBUTION_LEVELS,
    meta: {
      requiredAuthorization: true,
      roles: [APP_ROLES.ADMIN]
    }
  },
  {
    path: '/managements',
    name: PAGE_NAMES.MANAGEMENTS,
    component: () => import(/* webpackChunkName: "managements" */ '@/components/pages/managements/Managements.vue'),
    showItemInSideBar: true,
    showInSideBarAsPage: PAGE_NAMES.MANAGEMENTS,
    icon: 'icons/gear.svg',
    meta: {
      requiredAuthorization: true,
      roles: [APP_ROLES.ADMIN]
    }
  },
  {
    path: '/organization',
    name: PAGE_NAMES.ORGANIZATION,
    component: () => import(/* webpackChunkName: "organization" */ '@/components/pages/organization/Organization.vue'),
    showItemInSideBar: true,
    showInSideBarAsPage: PAGE_NAMES.ORGANIZATION,
    icon: 'icons/gear.svg',
    meta: {
      requiredAuthorization: true,
      roles: [APP_ROLES.ADMIN]
    }
  },

  {
    path: '/reward-management',
    name: PAGE_NAMES.REWARD_MANAGEMENT,
    component: () =>
      import(/* webpackChunkName: "reward-management" */ '@/components/pages/reward-management/RewardManagement.vue'),
    showItemInSideBar: true,
    showInSideBarAsPage: PAGE_NAMES.REWARD_MANAGEMENT,
    icon: 'icons/gear.svg',
    meta: {
      requiredAuthorization: true,
      roles: [APP_ROLES.ADMIN]
    }
  },
  {
    path: '/reward-period-detail/:id',
    name: PAGE_NAMES.REWARD_PERIOD_DETAIL,
    component: () =>
      import(
        /* webpackChunkName: "reward-period-detail" */ '../components/pages/reward-period-detail/RewardPeriodDetail.vue'
      ),
    showItemInSideBar: false,
    showInSideBarAsPage: PAGE_NAMES.REWARD_MANAGEMENT,
    meta: {
      requiredAuthorization: true,
      roles: [APP_ROLES.ADMIN]
    }
  },
  {
    path: '/welcome',
    name: PAGE_NAMES.WELCOME,
    component: () => import(/* webpackChunkName: "welcome" */ '../components/pages/welcome/Welcome.vue'),
    icon: 'mdi-poll',
    showItemInSideBar: false
  },
  {
    path: '/*',
    name: PAGE_NAMES.NOT_FOUND,
    icon: '',
    component: () => import(/* webpackChunkName: "not-found" */ '../components/pages/not-found/NotFound.vue'),
    showItemInSideBar: false
  }
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
});

router.beforeEach(async (to, from, next) => {
  const isLoginPage = to.name === PAGE_NAMES.LOGIN;
  if (!isLoginPage && !hasToken()) {
    next({ name: PAGE_NAMES.LOGIN });
  } else {
    const currentUser = store.getters['app/currentUser'];
    if (!currentUser) {
      await store.dispatch('app/fetchCurrentUser');
    }
    if (to.meta?.requiredAuthorization) {
      if (to.meta?.roles?.includes(APP_ROLES.PROJECT_ADMIN)) {
        await canVisitProjectAdminPage(to, next);
      } else if (to.meta?.roles?.includes(APP_ROLES.ADMIN)) {
        await canVisitAdminPage(next);
      }
    } else {
      next();
    }
  }
});

export { routes };
export default router;
